import type { ProductDto } from '@/api';
import CombinedCombobox from '@/components/combobox/combined-combobox';
import type { DefaultOptionType } from '@/components/combobox/combobox';
import {
  useFilteredProducts,
  useProductsPasting,
} from '@/components/combobox/combobox-queries';
import GroupsCombobox from '@/components/combobox/groups-combobox';
import PriceUnitSelection from '@/components/combobox/price-unit-selection';
import ThemesCombobox from '@/components/combobox/themes-combobox';
import { useErrorNotification } from '@/hooks/use-error-notification';
import { ROUTES } from '@/routes';
import {
  formatDateStringIso,
  getToday,
  parseDateString,
} from '@/utils/date-utils';
import {
  Button,
  Checkbox,
  DatePicker,
  HelpText,
  InlineAlert,
  Label,
  Modal,
} from '@lego/connect-components-react';
import { Flex } from '@lego/klik-ui';
import type { MultiValue } from 'chakra-react-select';
import { addYears, startOfYear } from 'date-fns';
import { type RefObject, useEffect, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { isApproveCreatePriceMode } from '../create-price-helpers';
import {
  useCheckForAlternativeVatForDate,
  useCreatePriceExperience,
} from '../create-price-queries';
import {
  type CreatePriceModalFilters,
  type CreatePriceMode,
  encodeCreatePriceParams,
} from '../create-price-search-params';

const TOP_ROW_LIMIT = 50000;

type CreatePriceRequest = {
  validFromDate: Date;
  productStatus: {
    firstHalfYear: boolean;
    secondHalfYear: boolean;
    carryOvers: boolean;
    outgoing: boolean;
  };
  priceStatus: {
    approved: boolean;
    preliminary: boolean;
  };
  onlyZeroPrices: boolean;
};

type CreatePriceModalProps = {
  isOpen: boolean;
  triggerRef: RefObject<HTMLElement | null>;
  onClose: () => void;
  mode: CreatePriceMode;
};

const CreatePriceModal = ({
  isOpen,
  triggerRef,
  onClose,
  mode,
}: CreatePriceModalProps) => {
  const isApproveMode = isApproveCreatePriceMode(mode);
  const navigate = useNavigate();
  const [selectedPriceUnitCategory, setSelectedPriceUnitCategory] =
    useState<string>('');
  const [selectedPriceUnits, setSelectedPriceUnits] = useState<
    DefaultOptionType[]
  >([]);
  const [selectedProducts, setSelectedProducts] = useState<DefaultOptionType[]>(
    [],
  );
  const [selectedThemes, setSelectedThemes] = useState<DefaultOptionType[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<DefaultOptionType[]>([]);
  const showError = useErrorNotification();
  const [modalNode, setModalNode] = useState<HTMLDivElement | null>(null);
  const { mutate: createPriceExperience, isPending } = useCreatePriceExperience(
    (error) => showError(error, 'create prices'),
  );

  const [form, setForm] = useState<CreatePriceRequest>({
    validFromDate: getToday(),
    productStatus: {
      firstHalfYear: false,
      secondHalfYear: false,
      carryOvers: false,
      outgoing: false,
    },
    priceStatus: {
      approved: false,
      // Approve mode: always filter by preliminary (checkbox hidden); Create mode: user-controlled
      preliminary: isApproveMode,
    },
    onlyZeroPrices: false,
  });

  const { data: hasAlternativeVat } = useCheckForAlternativeVatForDate(
    form.validFromDate ? form.validFromDate : null,
  );

  const [showVatAlert, setShowVatAlert] = useState(hasAlternativeVat);
  const [listSizeAlert, setListSizeAlert] = useState<'small' | 'large' | null>(
    null,
  );
  useEffect(() => {
    setShowVatAlert(hasAlternativeVat);
  }, [hasAlternativeVat]);

  const [hasError, setHasError] = useState(false);

  const isInPriceUnitCategoryMode = !!selectedPriceUnitCategory;

  const allFiltersEmpty =
    !isInPriceUnitCategoryMode &&
    selectedPriceUnits.length === 0 &&
    selectedProducts.length === 0 &&
    selectedThemes.length === 0 &&
    selectedGroups.length === 0;

  const canCreatePrice =
    form.validFromDate !== null &&
    !allFiltersEmpty &&
    !hasError &&
    listSizeAlert === null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset the alert when filters change
  useEffect(() => {
    setListSizeAlert(null);
  }, [
    selectedPriceUnitCategory,
    selectedPriceUnits,
    selectedProducts,
    selectedThemes,
    selectedGroups,
  ]);
  const handleSubmit = () => {
    const filters: CreatePriceModalFilters = {
      validFromDate: form.validFromDate,
      priceUnitCategoryId: isInPriceUnitCategoryMode
        ? Number.parseInt(selectedPriceUnitCategory)
        : null,
      priceUnitIds: isInPriceUnitCategoryMode
        ? []
        : selectedPriceUnits.map((item) => Number.parseInt(item.id)),
      productIds: selectedProducts.map((item) => Number.parseInt(item.id)),
      themeIds: selectedThemes.map((item) => item.id),
      groupIds: selectedGroups.map((item) => item.id),
      productStatus: form.productStatus,
      priceStatus: form.priceStatus,
      onlyZeroPrices: form.onlyZeroPrices,
    };

    createPriceExperience(
      {
        validFromDate: form.validFromDate,
        priceUnitCategoryId: isInPriceUnitCategoryMode
          ? Number.parseInt(selectedPriceUnitCategory)
          : undefined,
        priceUnitIds: isInPriceUnitCategoryMode
          ? undefined
          : filters.priceUnitIds,
        productIds: filters.productIds,
        themeIds: filters.themeIds,
        groupIds: filters.groupIds,
        productStatus: filters.productStatus,
        priceStatus: filters.priceStatus,
        onlyZeroPrices: filters.onlyZeroPrices,
      },
      {
        onSuccess: (res) => {
          if (res.length < 1) {
            setListSizeAlert('small');
            return;
          }
          if (res.length > TOP_ROW_LIMIT) {
            setListSizeAlert('large');
            return;
          }

          const headerNames = {
            priceUnits: selectedPriceUnits.map((item) =>
              item.label.replace(`${item.id} - `, ''),
            ),
            products: selectedProducts.map((item) =>
              item.label.replace(`${item.id} - `, ''),
            ),
            themes: selectedThemes.map((item) => item.label),
            groups: selectedGroups.map((item) =>
              item.label.replace(` - ${item.id}`, ''),
            ),
          };
          const searchParams = encodeCreatePriceParams(filters, mode);
          navigate(
            {
              pathname: generatePath(ROUTES.CreatePrice.path),
              search: searchParams.toString(),
            },
            { state: { headerNames, refetchKey: Date.now() } },
          );
          onClose();
        },
      },
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="large"
      triggerRef={triggerRef}
      ref={setModalNode}
    >
      <Modal.Title>
        {isApproveMode ? 'Approve Prices' : 'Create Prices'}
      </Modal.Title>
      <Modal.Content>
        {showVatAlert && (
          <InlineAlert
            showIcon
            variant="informative"
            isRemovable
            onDismiss={() => setShowVatAlert(false)}
          >
            <InlineAlert.Title>VAT Info</InlineAlert.Title>
            <InlineAlert.Content>
              An alternative VAT applies to one or more planning price lists.
              See Admin page for details.
            </InlineAlert.Content>
          </InlineAlert>
        )}
        {listSizeAlert !== null && (
          <InlineAlert
            showIcon
            variant="warning"
            isRemovable
            onDismiss={() => setListSizeAlert(null)}
          >
            <InlineAlert.Title>Please change your filters</InlineAlert.Title>
            <InlineAlert.Content>
              {listSizeAlert === 'small'
                ? 'No rows match the selected criteria.'
                : `The result will contain more than ${TOP_ROW_LIMIT} rows.`}
            </InlineAlert.Content>
          </InlineAlert>
        )}
        <div>
          <Label>Valid from date</Label>
          <DatePicker
            mode="single"
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            portalTarget={modalNode}
            maxDate={formatDateStringIso(startOfYear(addYears(new Date(), 3)))}
            value={formatDateStringIso(form.validFromDate)}
            onChange={(_, value) => {
              if (!value) return;

              setForm((prev) => ({
                ...prev,
                validFromDate: parseDateString(value),
              }));
            }}
          />
          <HelpText>Mandatory</HelpText>
        </div>
        <PriceUnitSelection
          selectedCategory={selectedPriceUnitCategory}
          onCategoryChange={setSelectedPriceUnitCategory}
          selectedPriceUnits={selectedPriceUnits}
          onPriceUnitsChange={setSelectedPriceUnits}
          setHasError={setHasError}
        />
        <CombinedCombobox<ProductDto>
          label="Products"
          useFilteredItems={useFilteredProducts}
          useItemsPasting={useProductsPasting}
          selectedItems={selectedProducts}
          onSelect={(selected) => setSelectedProducts([...selected])}
          setHasError={setHasError}
        />
        <ThemesCombobox
          selectedItems={selectedThemes}
          onSelect={(selected: MultiValue<DefaultOptionType>) =>
            setSelectedThemes([...selected])
          }
        />
        <GroupsCombobox
          selectedItems={selectedGroups}
          onSelect={(selected: MultiValue<DefaultOptionType>) =>
            setSelectedGroups([...selected])
          }
        />
        <div>
          <Label>Product status</Label>
          <Flex gap={'8px'}>
            <Checkbox
              label="1HY"
              title="First Half-year"
              aria-label="First Half-year"
              checked={form.productStatus.firstHalfYear}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  productStatus: {
                    ...prev.productStatus,
                    firstHalfYear: e.target.checked,
                  },
                }))
              }
            />
            <Checkbox
              label="2HY"
              title="Second Half-year"
              aria-label="Second Half-year"
              checked={form.productStatus.secondHalfYear}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  productStatus: {
                    ...prev.productStatus,
                    secondHalfYear: e.target.checked,
                  },
                }))
              }
            />
            <Checkbox
              label="C"
              title="Carry-overs"
              aria-label="Carry-overs"
              checked={form.productStatus.carryOvers}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  productStatus: {
                    ...prev.productStatus,
                    carryOvers: e.target.checked,
                  },
                }))
              }
            />
            <Checkbox
              label="O"
              title="Outgoing"
              aria-label="Outgoing"
              checked={form.productStatus.outgoing}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  productStatus: {
                    ...prev.productStatus,
                    outgoing: e.target.checked,
                  },
                }))
              }
            />
          </Flex>
        </div>
        {!isApproveMode && (
          <>
            <div>
              <Label>Price status</Label>
              <Flex gap={'8px'}>
                <Checkbox
                  label="Approved"
                  checked={form.priceStatus.approved}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      priceStatus: {
                        ...prev.priceStatus,
                        approved: e.target.checked,
                      },
                    }))
                  }
                />
                <Checkbox
                  label="Preliminary"
                  checked={form.priceStatus.preliminary}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      priceStatus: {
                        ...prev.priceStatus,
                        preliminary: e.target.checked,
                      },
                    }))
                  }
                />
              </Flex>
            </div>
            <div>
              <Label>Additional options</Label>
              <Checkbox
                label="Only zero prices"
                checked={form.onlyZeroPrices}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    onlyZeroPrices: e.target.checked,
                  }))
                }
              />
            </div>
          </>
        )}
      </Modal.Content>
      <Modal.Footer>
        <Button
          onClick={onClose}
          appearance="ghost"
          variant="neutral"
          aria-label={'Cancel'}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          appearance="solid"
          aria-label={isApproveMode ? 'Approve' : 'Create'}
          disabled={!canCreatePrice}
          loading={isPending}
        >
          {isApproveMode ? 'Approve' : 'Create'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
export default CreatePriceModal;
